import { API_BASE_URL } from '../config/api';
import { createSseParser } from './liveEventsService';
import { authFetch, getAccessToken } from './session';

/**
 * KI-Schreibhilfe im Live-Chat (notes/livecenter-contract-2026-09-30.md §5).
 *
 * - `improve` streams a staff draft rewritten as a WhatsApp message
 *   (SSE `delta` … then `done` or `error`), read with fetch + ReadableStream
 *   because EventSource cannot POST or send the Bearer header.
 * - `transcribe` uploads a dictation and returns its text.
 *
 * Every failure is a `ComposeError` with a code the UI turns into German
 * text; an abort (the caller's AbortSignal) is rethrown as the AbortError it is.
 */

export const COMPOSE_IMPROVE_URL = `${API_BASE_URL}/live-chat/compose/improve`;
export const COMPOSE_TRANSCRIBE_URL = `${API_BASE_URL}/live-chat/compose/transcribe`;
/** No byte for this long = dead stream. The server gives the model 30 s in total. */
export const IMPROVE_STALL_TIMEOUT_MS = 40_000;
/** Upload + transcription; the server allows 60 s for the upload and 30 s for OpenAI. */
export const TRANSCRIBE_TIMEOUT_MS = 95_000;

export type FlaggedKind = 'time' | 'date' | 'price' | 'name' | 'number';

/** A value the model put into the message that was neither in the draft nor in the chat. */
export interface FlaggedFact {
  value: string;
  kind: FlaggedKind;
}

export interface ImproveResult {
  text: string;
  flagged: FlaggedFact[];
}

export type ComposeErrorCode =
  | 'rate_limited'
  | 'invalid_text'
  | 'upstream'
  | 'not_found'
  | 'too_large'
  | 'too_short'
  | 'unsupported_format'
  | 'network'
  | 'auth';

export class ComposeError extends Error {
  readonly code: ComposeErrorCode;

  constructor(code: ComposeErrorCode) {
    super(`compose failed: ${code}`);
    this.name = 'ComposeError';
    this.code = code;
  }
}

export interface ComposeRequestOptions {
  /**
   * Test seams. The defaults are the session's own: a token renewed when it is
   * about to expire, and a fetch whose 401 is final (the session is over and
   * the app has been told).
   */
  fetchImpl?: typeof fetch;
  getToken?: () => string | null | Promise<string | null>;
}

export function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

const SERVER_ERROR_CODES: ReadonlySet<string> = new Set(['rate_limited', 'invalid_text', 'upstream']);

async function authHeaders(getToken: NonNullable<ComposeRequestOptions['getToken']>): Promise<Record<string, string>> {
  let token: string | null;
  try {
    token = await getToken();
  } catch {
    // The login could not be renewed because the server is unreachable.
    throw new ComposeError('network');
  }
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function errorForStatus(status: number, detail?: unknown): ComposeError {
  switch (status) {
    case 401:
      // Final: authFetch already tried to renew the session and told the app.
      return new ComposeError('auth');
    case 404:
      return new ComposeError('not_found');
    case 408: // the upload took too long (slow connection)
      return new ComposeError('network');
    case 413:
      return new ComposeError('too_large');
    case 415:
      return new ComposeError('unsupported_format');
    case 422:
      return new ComposeError(detail === 'audio_too_short' ? 'too_short' : 'invalid_text');
    case 429:
      return new ComposeError('rate_limited');
    default:
      return new ComposeError('upstream');
  }
}

async function send(fetchImpl: typeof fetch, url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetchImpl(url, init);
  } catch (error) {
    if (isAbortError(error) || init.signal?.aborted) throw error;
    throw new ComposeError('network');
  }
}

/** Abort `target` when `source` aborts (the caller's signal drives our own controller). */
function follow(source: AbortSignal | undefined, target: AbortController): () => void {
  if (!source) return () => {};
  const onAbort = () => target.abort();
  if (source.aborted) target.abort();
  else source.addEventListener('abort', onAbort);
  return () => source.removeEventListener('abort', onAbort);
}

function abortError(): Error {
  return Object.assign(new Error('The operation was aborted.'), { name: 'AbortError' });
}

/**
 * Rewrite `text` for the contact. `onDelta` receives each streamed piece;
 * the promise resolves with the server's final text (the source of truth —
 * it is cleaned up, so it can differ from the concatenated deltas).
 */
export async function improve(
  contactKey: string,
  text: string,
  onDelta: (delta: string) => void,
  signal?: AbortSignal,
  options: ComposeRequestOptions = {},
): Promise<ImproveResult> {
  const fetchImpl: typeof fetch = options.fetchImpl ?? authFetch;
  const getToken = options.getToken ?? getAccessToken;
  if (signal?.aborted) throw abortError();

  const controller = new AbortController();
  const unfollow = follow(signal, controller);
  let stalled = false;
  let watchdog: ReturnType<typeof setTimeout> | null = null;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  let response: Response | null = null;
  const armWatchdog = () => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      stalled = true;
      controller.abort();
      reader?.cancel().catch(() => undefined);
    }, IMPROVE_STALL_TIMEOUT_MS);
  };

  try {
    armWatchdog();
    response = await send(fetchImpl, COMPOSE_IMPROVE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(await authHeaders(getToken)) },
      body: JSON.stringify({ contact_key: contactKey, text }),
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok || !response.body) throw errorForStatus(response.status);

    let result: ImproveResult | null = null;
    let failure: ComposeError | null = null;
    const parser = createSseParser((frame) => {
      let data: Record<string, unknown>;
      try {
        data = JSON.parse(frame.data);
      } catch {
        return; // a malformed frame never ends the stream by itself
      }
      if (frame.event === 'delta' && typeof data.text === 'string') {
        onDelta(data.text);
      } else if (frame.event === 'done' && typeof data.text === 'string') {
        result = { text: data.text, flagged: Array.isArray(data.flagged) ? (data.flagged as FlaggedFact[]) : [] };
      } else if (frame.event === 'error') {
        const code = typeof data.code === 'string' && SERVER_ERROR_CODES.has(data.code) ? data.code : 'upstream';
        failure = new ComposeError(code as ComposeErrorCode);
      }
    });

    const body = response.body.getReader();
    reader = body;
    const decoder = new TextDecoder();
    while (!result && !failure) {
      const { done, value } = await body.read();
      if (done) break;
      armWatchdog();
      parser.push(decoder.decode(value, { stream: true }));
    }
    if (!result && !failure) {
      parser.push(decoder.decode());
      parser.flush();
    }
    if (failure) throw failure;
    if (!result) throw new ComposeError('upstream');
    return result;
  } catch (error) {
    if (stalled) throw new ComposeError('network');
    if (signal?.aborted) throw isAbortError(error) ? error : abortError();
    throw error;
  } finally {
    if (watchdog) clearTimeout(watchdog);
    unfollow();
    // Every exit (done, error event, HTTP error, abort) releases the connection.
    releaseBody(reader, response);
  }
}

/** Cancel the response body; never throws (it runs in `finally`). */
function releaseBody(reader: ReadableStreamDefaultReader<Uint8Array> | null, response: Response | null): void {
  try {
    const pending = reader ? reader.cancel() : response?.body?.cancel();
    pending?.catch(() => undefined);
  } catch {
    // already closed or locked elsewhere
  }
}

const EXTENSIONS: Record<string, string> = {
  'audio/webm': 'webm',
  'video/webm': 'webm',
  'audio/ogg': 'ogg',
  'audio/mp4': 'mp4',
  'video/mp4': 'mp4',
  'audio/x-m4a': 'm4a',
  'audio/aac': 'm4a',
  'audio/wav': 'wav',
  'audio/mpeg': 'mp3',
};

/** File name for the upload; the server trusts the bytes, the name only helps logs and tools. */
export function dictationFileName(mimeType: string): string {
  const base = mimeType.split(';')[0].trim().toLowerCase();
  return `diktat.${EXTENSIONS[base] ?? 'webm'}`;
}

/** Turn a dictation into text. An empty string means nothing intelligible was said. */
export async function transcribe(
  contactKey: string,
  audio: Blob,
  signal?: AbortSignal,
  options: ComposeRequestOptions = {},
): Promise<string> {
  const fetchImpl: typeof fetch = options.fetchImpl ?? authFetch;
  const getToken = options.getToken ?? getAccessToken;

  const form = new FormData();
  form.append('audio', audio, dictationFileName(audio.type));
  form.append('contact_key', contactKey);

  const controller = new AbortController();
  const unfollow = follow(signal, controller);
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, TRANSCRIBE_TIMEOUT_MS);
  try {
    // No Content-Type header: the browser adds multipart/form-data with its boundary.
    const response = await send(fetchImpl, COMPOSE_TRANSCRIBE_URL, {
      method: 'POST',
      headers: { Accept: 'application/json', ...(await authHeaders(getToken)) },
      body: form,
      signal: controller.signal,
    });
    if (!response.ok) {
      const detail = await response
        .json()
        .then((body: { detail?: unknown }) => body?.detail)
        .catch(() => undefined);
      throw errorForStatus(response.status, detail);
    }
    const body = (await response.json().catch(() => null)) as { text?: unknown } | null;
    if (!body || typeof body.text !== 'string') throw new ComposeError('upstream');
    return body.text;
  } catch (error) {
    if (timedOut) throw new ComposeError('network');
    throw error;
  } finally {
    clearTimeout(timer);
    unfollow();
  }
}
